import './applicantEditResidencePage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString, uniqueId } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AddressLineOne } from '../../../components/shared_new/AddressLineOne/AddressLineOne';
import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { USStates } from '../../../constants/states';
import { housingTypeOptions } from '../../../store/applicantStore/constants/housingTypeOptions';
import { monthsAtResidenceOptions } from '../../../store/applicantStore/constants/monthsAtResidenceOptions';
import { ONE_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/oneYear';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { inputEventHandler } from '../../../utils/component/inputEventHandler';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();

  const {
    computed: { reviewEditPathname, reviewEditProgress },
    currentResidenceCity,
    currentResidenceLineOne,
    currentResidenceLineTwo,
    currentResidenceMonthlyPaymentAmount,
    currentResidenceMonthsAt,
    currentResidencePostalCode,
    currentResidenceState,
    currentResidenceType,
    isSyncingResidence,
    previousResidenceCity,
    previousResidenceLineOne,
    previousResidenceLineTwo,
    previousResidencePostalCode,
    previousResidenceState,
    syncingResidenceFailedMessage,
    setCurrentResidenceCity,
    setCurrentResidenceLineOne,
    setCurrentResidenceLineTwo,
    setCurrentResidenceMonthlyPaymentAmount,
    setCurrentResidenceMonthsAt,
    setCurrentResidencePostalCode,
    setCurrentResidenceState,
    setCurrentResidenceType,
    setPreviousResidenceCity,
    setPreviousResidenceLineOne,
    setPreviousResidenceLineTwo,
    setPreviousResidencePostalCode,
    setPreviousResidenceState,
    syncResidence,
  } = useApplicantStore();

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    delayError: 200,
    defaultValues: {
      currentCityValue: currentResidenceCity,
      currentLineOneValue: currentResidenceLineOne,
      currentLineTwoValue: currentResidenceLineTwo,
      currentMonthsAtResidenceValue: currentResidenceMonthsAt,
      currentPostalCodeValue: currentResidencePostalCode,
      currentStateValue: currentResidenceState,
      housingAmountValue: inputEventHandler.currency.formatter(
        currentResidenceMonthlyPaymentAmount,
      ),
      housingTypeValue: currentResidenceType,
      previousCityValue: previousResidenceCity,
      previousLineOneValue: previousResidenceLineOne,
      previousLineTwoValue: previousResidenceLineTwo,
      previousPostalCodeValue: previousResidencePostalCode,
      previousStateValue: previousResidenceState,
    },
    mode: 'onBlur',
    shouldFocusError: false,
  });

  const shouldShowPreviousResidenceFields =
    watch('currentMonthsAtResidenceValue', currentResidenceMonthsAt) ===
    ONE_YEAR_IN_MONTHS_AS_STRING;

  const housingTypeValidation = {
    required: t('validation.housingType.required'),
  };

  const housingAmountValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.housingAmount.required'),
    min: {
      value: 0,
      message: t('validation.housingAmount.min', { min: '$0' }),
    },
    max: {
      value: 1000000,
      message: t('validation.housingAmount.max', { max: '$1,000,000' }),
    },
  };

  const addressValidation = {
    required: t('validation.address.required'),
  };

  const cityValidation = {
    required: t('validation.city.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const postalCodeValidation = {
    required: t('validation.zipCode.required'),
    minLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
    maxLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
  };

  const monthsAtResidenceValidation = {
    required: t('validation.housingType.required'),
  };

  const finishEditing = (): void => {
    navigate(reviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      currentCityValue,
      currentLineOneValue,
      currentLineTwoValue,
      currentMonthsAtResidenceValue,
      currentPostalCodeValue,
      currentStateValue,
      housingAmountValue,
      housingTypeValue,
      previousCityValue,
      previousLineOneValue,
      previousLineTwoValue,
      previousPostalCodeValue,
      previousStateValue,
    } = values;

    if (
      !currentCityValue ||
      !currentLineOneValue ||
      !currentMonthsAtResidenceValue ||
      !currentPostalCodeValue ||
      !currentStateValue ||
      !housingAmountValue ||
      !housingTypeValue
    ) {
      return;
    }

    if (
      shouldShowPreviousResidenceFields &&
      (!previousLineOneValue ||
        !previousCityValue ||
        !previousStateValue ||
        !previousPostalCodeValue)
    ) {
      return;
    }

    setCurrentResidenceCity(currentCityValue);
    setCurrentResidenceLineOne(currentLineOneValue);
    setCurrentResidenceLineTwo(currentLineTwoValue);
    setCurrentResidenceMonthlyPaymentAmount(housingAmountValue);
    setCurrentResidenceMonthsAt(currentMonthsAtResidenceValue);
    setCurrentResidencePostalCode(currentPostalCodeValue);
    setCurrentResidenceState(currentStateValue);
    setCurrentResidenceType(housingTypeValue);

    if (shouldShowPreviousResidenceFields) {
      setPreviousResidenceCity(previousCityValue);
      setPreviousResidenceLineOne(previousLineOneValue);
      setPreviousResidenceLineTwo(previousLineTwoValue);
      setPreviousResidencePostalCode(previousPostalCodeValue);
      setPreviousResidenceState(previousStateValue);
    } else {
      setPreviousResidenceCity(null);
      setPreviousResidenceLineOne(null);
      setPreviousResidenceLineTwo(null);
      setPreviousResidencePostalCode(null);
      setPreviousResidenceState(null);
    }

    if (isDirty) {
      const synced = await syncResidence();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-edit-residence-page" sx={theme.pageLayout}>
        <header data-testid={locator.pages.applicantEditResidence.header.root}>
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.applicantEditResidence.header.previousStepButton
            }
            type="button"
            onClick={finishEditing}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.applicantEditResidence.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.applicantEditResidence.progress.root}
            value={reviewEditProgress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.applicantEditResidence.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantEditResidence.form.h1}>
                {t('ApplicantEditResidencePage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantEditResidence.form.h2}>
                {t('ApplicantEditResidencePage.subtitle')}
              </h2>

              {!isEmpty(syncingResidenceFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantEditResidence.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingResidenceFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register('housingTypeValue', housingTypeValidation)}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    error={!isEmpty(errors.housingTypeValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.applicantEditResidence.form
                          .residenceTypeSelect,
                    }}
                    label={t('ApplicantEditResidencePage.housingType')}
                    value={watch('housingTypeValue', currentResidenceType)}
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('ApplicantEditResidencePage.housingType')}
                    </MenuItem>

                    {housingTypeOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.housingTypeValue) && (
                    <span
                      data-testid={
                        locator.pages.applicantEditResidence.form
                          .residenceTypeSelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.housingTypeValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('housingAmountValue', housingAmountValidation)}
                    error={!isEmpty(errors.housingAmountValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.applicantEditResidence.form.amountInput,
                      inputMode: 'numeric',
                    }}
                    label={t('ApplicantEditResidencePage.housingAmount')}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'housingAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'housingAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'housingAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.housingAmountValue) && (
                    <span
                      data-testid={
                        locator.pages.applicantEditResidence.form
                          .amountInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.housingAmountValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <AddressLineOne
                    errorTestId={
                      locator.pages.applicantEditResidence.form
                        .addressLine1InputError
                    }
                    errors={errors}
                    inputProps={{
                      'data-testid':
                        locator.pages.applicantEditResidence.form
                          .addressLine1Input,
                    }}
                    label={t('ApplicantEditResidencePage.residenceLineOne')}
                    name="currentLineOneValue"
                    register={register}
                    setValue={setValue}
                    suggestionFields={{
                      cityFieldName: 'currentCityValue',
                      stateFieldName: 'currentStateValue',
                      postalCodeFieldName: 'currentPostalCodeValue',
                    }}
                    validation={addressValidation}
                    variant="outlined"
                    fullWidth
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('currentLineTwoValue')}
                        autoComplete="address-line2"
                        inputProps={{
                          'data-testid':
                            locator.pages.applicantEditResidence.form
                              .addressLine2Input,
                        }}
                        label={t('ApplicantEditResidencePage.residenceLineTwo')}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('currentCityValue', cityValidation)}
                        InputLabelProps={{
                          shrink: Boolean(watch('currentCityValue')),
                        }}
                        autoComplete="address-level2"
                        error={!isEmpty(errors.currentCityValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.applicantEditResidence.form.cityInput,
                        }}
                        label={t('ApplicantEditResidencePage.residenceCity')}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.currentCityValue) && (
                        <span
                          data-testid={
                            locator.pages.applicantEditResidence.form
                              .cityInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.currentCityValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('currentStateValue', stateValidation)}
                        InputLabelProps={{
                          shrink: Boolean(watch('currentStateValue')),
                        }}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        autoComplete="address-level1"
                        error={!isEmpty(errors.currentStateValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.applicantEditResidence.form
                              .stateSelect,
                        }}
                        label={t('ApplicantEditResidencePage.residenceState')}
                        value={watch(
                          'currentStateValue',
                          currentResidenceState || '',
                        )}
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('ApplicantEditResidencePage.residenceState')}
                        </MenuItem>

                        {USStates.map((stateObj) => {
                          return (
                            <MenuItem
                              key={stateObj.abbreviation}
                              value={stateObj.abbreviation}
                            >
                              {stateObj.name}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.currentStateValue) && (
                        <span
                          data-testid={
                            locator.pages.applicantEditResidence.form
                              .stateSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.currentStateValue.message)}
                        </span>
                      )}
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register(
                          'currentPostalCodeValue',
                          postalCodeValidation,
                        )}
                        InputLabelProps={{
                          shrink: Boolean(watch('currentPostalCodeValue')),
                        }}
                        autoComplete="postal-code"
                        error={!isEmpty(errors.currentPostalCodeValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.applicantEditResidence.form
                              .zipCodeInput,
                          inputMode: 'numeric',
                        }}
                        label={t('ApplicantEditResidencePage.zip')}
                        type="number"
                        variant="outlined"
                        fullWidth
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.currentPostalCodeValue) && (
                        <span
                          data-testid={
                            locator.pages.applicantEditResidence.form
                              .zipCodeInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.currentPostalCodeValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register(
                      'currentMonthsAtResidenceValue',
                      monthsAtResidenceValidation,
                    )}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    error={!isEmpty(errors.currentMonthsAtResidenceValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.applicantEditResidence.form.monthSelect,
                    }}
                    label={t('ApplicantEditResidencePage.duration')}
                    value={watch(
                      'currentMonthsAtResidenceValue',
                      currentResidenceMonthsAt || '',
                    )}
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('ApplicantEditResidencePage.duration')}
                    </MenuItem>

                    {monthsAtResidenceOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.currentMonthsAtResidenceValue) && (
                    <span
                      data-testid={
                        locator.pages.applicantEditResidence.form
                          .monthSelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.currentMonthsAtResidenceValue.message)}
                    </span>
                  )}
                </div>
              </div>

              {shouldShowPreviousResidenceFields && (
                <div
                  data-testid={
                    locator.pages.applicantEditResidence.form
                      .previousResidenceRoot
                  }
                  style={{ marginBottom: '3rem' }}
                >
                  <h3>{t('ApplicantEditResidencePage.previousResidence')}</h3>

                  <div style={{ marginTop: '1.5rem' }}>
                    <AddressLineOne
                      errorTestId={
                        locator.pages.applicantEditResidence.form
                          .previousAddressLine1InputError
                      }
                      errors={errors}
                      inputProps={{
                        'data-testid':
                          locator.pages.applicantEditResidence.form
                            .previousAddressLine1Input,
                      }}
                      label={t('ApplicantEditResidencePage.residenceLineOne')}
                      name="previousLineOneValue"
                      register={register}
                      setValue={setValue}
                      suggestionFields={{
                        cityFieldName: 'previousCityValue',
                        stateFieldName: 'previousStateValue',
                        postalCodeFieldName: 'previousPostalCodeValue',
                      }}
                      validation={addressValidation}
                      variant="outlined"
                      fullWidth
                    />
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <div className="inline-inputs">
                      <div className="input-half">
                        <TextField
                          {...register('previousLineTwoValue')}
                          autoComplete="address-line2"
                          inputProps={{
                            'data-testid':
                              locator.pages.applicantEditResidence.form
                                .previousAddressLine2Input,
                          }}
                          label={t(
                            'ApplicantEditResidencePage.residenceLineTwo',
                          )}
                          variant="outlined"
                          fullWidth
                        />
                      </div>

                      <div className="input-half">
                        <TextField
                          {...register('previousCityValue', cityValidation)}
                          InputLabelProps={{
                            shrink: Boolean(watch('previousCityValue')),
                          }}
                          autoComplete="address-level2"
                          error={!isEmpty(errors.previousCityValue)}
                          inputProps={{
                            'data-testid':
                              locator.pages.applicantEditResidence.form
                                .previousCityInput,
                          }}
                          label={t('ApplicantEditResidencePage.residenceCity')}
                          variant="outlined"
                          fullWidth
                        />

                        {!isEmpty(errors.previousCityValue) && (
                          <span
                            data-testid={
                              locator.pages.applicantEditResidence.form
                                .previousCityInputError
                            }
                            style={{ fontSize: '12px', color: red[700] }}
                          >
                            {toString(errors.previousCityValue.message)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <div className="inline-inputs">
                      <div className="input-half">
                        <TextField
                          {...register('previousStateValue', stateValidation)}
                          InputLabelProps={{
                            shrink: Boolean(watch('previousStateValue')),
                          }}
                          SelectProps={{
                            IconComponent: ExpandMoreRoundedIcon,
                          }}
                          autoComplete="address-level1"
                          error={!isEmpty(errors.previousStateValue)}
                          inputProps={{
                            'data-testid':
                              locator.pages.applicantEditResidence.form
                                .previousStateSelect,
                          }}
                          label={t('ApplicantEditResidencePage.residenceState')}
                          value={
                            watch(
                              'previousStateValue',
                              previousResidenceState || '',
                            ) || ''
                          }
                          fullWidth
                          select
                        >
                          <MenuItem value="" disabled>
                            {t('ApplicantEditResidencePage.residenceState')}
                          </MenuItem>

                          {USStates.map((stateObj) => {
                            return (
                              <MenuItem
                                key={uniqueId(stateObj.abbreviation)}
                                value={stateObj.abbreviation}
                              >
                                {stateObj.name}
                              </MenuItem>
                            );
                          })}
                        </TextField>

                        {!isEmpty(errors.previousStateValue) && (
                          <span
                            data-testid={
                              locator.pages.applicantEditResidence.form
                                .previousStateSelectError
                            }
                            style={{ fontSize: '12px', color: red[700] }}
                          >
                            {toString(errors.previousStateValue.message)}
                          </span>
                        )}
                      </div>

                      <div className="input-half">
                        <TextField
                          {...register(
                            'previousPostalCodeValue',
                            postalCodeValidation,
                          )}
                          InputLabelProps={{
                            shrink: Boolean(watch('previousPostalCodeValue')),
                          }}
                          autoComplete="postal-code"
                          error={!isEmpty(errors.previousPostalCodeValue)}
                          inputProps={{
                            'data-testid':
                              locator.pages.applicantEditResidence.form
                                .previousZipCodeInput,
                            inputMode: 'numeric',
                          }}
                          label={t('ApplicantEditResidencePage.zip')}
                          type="number"
                          variant="outlined"
                          fullWidth
                          onKeyDown={inputEventHandler.number.keydown}
                          onPasteCapture={inputEventHandler.number.pasteCapture}
                        />

                        {!isEmpty(errors.previousPostalCodeValue) && (
                          <span
                            data-testid={
                              locator.pages.applicantEditResidence.form
                                .previousZipCodeInputError
                            }
                            style={{ fontSize: '12px', color: red[700] }}
                          >
                            {toString(errors.previousPostalCodeValue.message)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <LoadingButton
                    color="secondary"
                    data-testid={
                      locator.pages.applicantEditResidence.form.cancelButton
                    }
                    loadingPosition="start"
                    startIcon={<></>}
                    type="button"
                    variant="contained"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('ApplicantEditResidencePage.cancel')}
                  </LoadingButton>

                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.applicantEditResidence.form.submitButton
                    }
                    disabled={!isValid}
                    loading={isSyncingResidence}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('ApplicantEditResidencePage.save')}
                  </LoadingButton>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantEditResidence.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantEditResidencePage';
